import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable, Sort } from '@angular/material/sort';
import Hotjar from '@hotjar/browser';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { EMPTY, firstValueFrom, merge, Subscription } from 'rxjs';
import { finalize, first, switchMap, tap } from 'rxjs/operators';
import { Permission } from 'src/app/common/enums';
import { Messages } from 'src/app/common/enums/messages';
import { ScreenService } from 'src/app/core/screen.service';
import { AbsenceService } from 'src/app/data/absence.service';
import { Filter } from 'src/app/models/common/filter';
import { AbsenceSyncStatusEnum } from 'src/app/models/enums/absence-sync-status-enum';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { WorkersTimesheetsListType } from 'src/app/workers/workers-timesheets/workers-timesheets.component';
import { SickLeaveModalComponent } from '../sick-leaves-modal/sick-leaves-modal.component';
import { AuthService } from './../../core/authentication/auth.service';
import { AbsenceListDataSource } from './absence-list.datasource';

const DEFAULT_NAME_SORT_FIELD = 'fullName';
const ALTERNATIVE_NAME_SORT_FIELD = 'lastName';

@Component({
  selector: 'app-absence-list',
  templateUrl: './absence-list.component.html',
  styleUrls: ['./absence-list.component.scss'],
  providers: [AbsenceListDataSource, DatePipe],
  standalone: false
})
export class AbsenceListComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  screenService = inject(ScreenService);
  private absenceService = inject(AbsenceService);
  private dialog = inject(MatDialog);
  private translateService = inject(TranslateService);
  private snackbar = inject(SnackBarService);
  private spinner = inject(NgxSpinnerService);
  private authService = inject(AuthService);
  dataSource = inject(AbsenceListDataSource, { self: true });
  readonly isSentToExternalSystemVisible = this.authService.hasPermission(Permission.ShowIsSentToExteralSystemFilter);

  displayedColumns = [
    'lastName',
    'company',
    'employer',
    'employerObject',
    'absenceType',
    'sentToApprovalOn',
    'startDate',
    'businessDays',
    'workingTimeDimension',
    'absenceReason',
    'status',
  ];

  @Input() filters: Filter[] = [];
  @Input() listType!: WorkersTimesheetsListType;
  @Input() defaultSort!: Sort;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  private subscriptions: Subscription[] = [];
  readonly spinnerName = 'absencesListGrid';


  constructor() {
    if (this.isSentToExternalSystemVisible) {
      this.displayedColumns.push('isSentToExteralSystem');
      this.displayedColumns.push('actions');
    } else {
      this.displayedColumns.push('actions');
    }
  }

  get AbsenceSyncStatusEnum() {
    return AbsenceSyncStatusEnum;
  }

  ngOnInit(): void {
    Hotjar.event('lista nieobecności!');
  }

  ngOnDestroy(): void {
    this.spinner.hide(this.spinnerName);
    if (this.subscriptions && this.subscriptions.length) {
      this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0)));
    this.subscriptions.push(this.translateService.onLangChange.subscribe(() => (this.paginator.pageIndex = 0)));

    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page, this.translateService.onLangChange).subscribe(() => this.fetchAbsences()),
    );

    this.subscriptions.push(
      this.dataSource.loading$.subscribe((loading) =>
        loading ? this.spinner.show(this.spinnerName) : this.spinner.hide(this.spinnerName)
      ),
    );

    this.fetchAbsences(true);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.dataSource) return;

    if (!!changes.defaultSort && !changes.defaultSort.firstChange) {
      const defaultSort = changes.defaultSort.currentValue as Sort;

      if (defaultSort.active != this.sort.active || defaultSort.direction != this.sort.direction) {
        this.sort.sort({ id: defaultSort.active, start: defaultSort.direction } as MatSortable);
      }
    }

    if (this.paginator) {
      this.fetchAbsences(true);
    }
  }

  sendToApproval(absenceId: number): void {
    const onConfirm = (absenceId: number) => {
      this.spinner.show();
      return this.absenceService.sendToApproval(absenceId).pipe(
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullySentToApprovalAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmSendingToApprovalAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
      )
      .subscribe();
  }

  confirmAbsence(absenceId: number) {
    const onConfirm = (absenceId: number) => {
      this.spinner.show();
      return this.absenceService.confirmAbsence(absenceId).pipe(
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyConfirmedAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmConfirmingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
      )
      .subscribe();
  }

  deleteAbsence(absenceId: number) {
    const onConfirm = (absenceId: number) => {
      this.spinner.show();
      return this.absenceService.deleteAbsence(absenceId).pipe(
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyDeletedAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmDeletingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
      )
      .subscribe();
  }

  backToDraftAbsence(absenceId: number) {
    const onConfirm = (absenceId: number) => {
      this.spinner.show();
      return this.absenceService.backToDraftAbsence(absenceId).pipe(
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyBackToDraftAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmBackToDraftAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
      )
      .subscribe();
  }

  acceptAbsence(absenceId: number) {
    const onConfirm = () => {
      this.spinner.show();
      return this.absenceService.acceptAbsence(absenceId).pipe(
        first(),
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccesfullyAcceptedAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmAcceptingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
      )
      .subscribe();
  }

  rejectAbsence(absenceId: number) {
    const onConfirm = () => {
      this.spinner.show();
      return this.absenceService.rejectAbsence(absenceId).pipe(
        first(),
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccesfullyRejectedAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmRejectingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
      )
      .subscribe();
  }

  async cancelAbsence(absenceId: number) {
    const onConfirm = () => {
      this.spinner.show();
      return this.absenceService.cancelAbsence(absenceId).pipe(
        first(),
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyCanceledAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmCancellingAbsenceMessage } })
        .afterClosed()
        .pipe(
          first(),
          switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
        ),
    );
  }

  openDetailsModal(sickLeaveId: number) {
    this.dialog
      .open(SickLeaveModalComponent, {
        data: { SickLeaveId: sickLeaveId },
      })
      .afterClosed()
      .pipe(first())
      .subscribe((isCreated: boolean) => {
        if (isCreated) {
          this.fetchAbsences();
        }
      });
  }

  private fetchAbsences(resetPage: boolean = false) {
    if (resetPage) {
      this.paginator.pageIndex = 0;
    }

    this.dataSource.fetchAbsences(
      this.paginator.pageIndex + 1,
      this.paginator.pageSize,
      this.sort.active !== DEFAULT_NAME_SORT_FIELD ? this.sort.active : ALTERNATIVE_NAME_SORT_FIELD,
      this.sort.direction,
      this.filters,
    );
  }
}
